/**
 * 把报告正文里的常见 Markdown 转成 HTML。先转义原文，再套标签。
 * 覆盖标题、加粗、有序/无序列表、表格和段落。
 */

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function inline(value) {
    return escapeHtml(value).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

function isTableRow(line) {
    const text = line.trim();
    return text.startsWith("|") && text.endsWith("|") && text.length > 1;
}

function isTableSep(line) {
    return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

function splitCells(line) {
    let text = line.trim();
    if (text.startsWith("|")) text = text.slice(1);
    if (text.endsWith("|")) text = text.slice(0, -1);
    return text.split("|").map(cell => cell.trim());
}

function headingLine(line) {
    return /^(#{1,6})\s+(.+)$/.exec(line);
}

/**
 * @param {string} source 模型返回的 Markdown 正文
 * @returns {string} 未消毒的 HTML，调用方需再做净化
 */
export function toMarkdownHtml(source) {
    const lines = String(source || "").replace(/\r\n/g, "\n").split("\n");
    const html = [];
    let index = 0;
    while (index < lines.length) {
        const line = lines[index];
        if (!line.trim()) {
            index += 1;
            continue;
        }
        if (line.trim().startsWith("```")) {
            const block = [];
            index += 1;
            while (index < lines.length && !lines[index].trim().startsWith("```")) {
                block.push(escapeHtml(lines[index]));
                index += 1;
            }
            if (index < lines.length) index += 1;
            html.push(`<pre><code>${block.join("\n")}</code></pre>`);
            continue;
        }
        const heading = headingLine(line);
        if (heading) {
            const level = heading[1].length;
            html.push(`<h${level}>${inline(heading[2])}</h${level}>`);
            index += 1;
            continue;
        }
        if (isTableRow(line) && index + 1 < lines.length && isTableSep(lines[index + 1])) {
            const header = splitCells(line);
            index += 2;
            const rows = [];
            while (index < lines.length && isTableRow(lines[index])) {
                rows.push(splitCells(lines[index]));
                index += 1;
            }
            const head = header.map(cell => `<th>${inline(cell)}</th>`).join("");
            const body = rows.map(row => `<tr>${row.map(cell => `<td>${inline(cell)}</td>`).join("")}</tr>`).join("");
            html.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
            continue;
        }
        if (/^\s*[-*]\s+/.test(line)) {
            const items = [];
            while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
                items.push(inline(lines[index].replace(/^\s*[-*]\s+/, "")));
                index += 1;
            }
            html.push(`<ul>${items.map(item => `<li>${item}</li>`).join("")}</ul>`);
            continue;
        }
        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
                items.push(inline(lines[index].replace(/^\s*\d+\.\s+/, "")));
                index += 1;
            }
            html.push(`<ol>${items.map(item => `<li>${item}</li>`).join("")}</ol>`);
            continue;
        }
        const paragraph = [line];
        index += 1;
        while (index < lines.length && lines[index].trim()
            && !headingLine(lines[index])
            && !/^\s*[-*]\s+/.test(lines[index])
            && !/^\s*\d+\.\s+/.test(lines[index])
            && !lines[index].trim().startsWith("```")
            && !(isTableRow(lines[index]) && index + 1 < lines.length && isTableSep(lines[index + 1]))) {
            paragraph.push(lines[index]);
            index += 1;
        }
        html.push(`<p>${paragraph.map(inline).join("<br>")}</p>`);
    }
    return html.join("");
}
